import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router-dom';

import {GROUPS_ROUTES} from '../../groups/models/constants';
import {SENDER} from '../interfaces';
import {useSplitBillsGroupViewModelProvider} from '../SplitBillsGroupViewModelContext';
import {useSplitBillsViewModelProvider} from '../SplitBillsViewModelContext';
import {MiaLoadingAvatarV1} from '../styles/constants';

import {MessageBubble} from './MessageBubble';
import {MessageInput} from './MessageInput';
import {SplitResultGroupActions} from './SplitResultGroupActions';
import {SplitShareLinkCard} from './SplitShareLinkCard';

const TypingBubble = memo(({text}: {text: string}) => (
  <div className="chat-bubble-wrapper bot">
    <img
      src={MiaLoadingAvatarV1}
      alt="Mia avatar"
      className="chat-bubble-avatar"
    />
    <div className="chat-bubble bot typing">
      <span className="gradient-text">{text}</span>
    </div>
  </div>
));

export const ChatBot = () => {
  const {groupId, groupName, inviterName} =
    useSplitBillsGroupViewModelProvider();

  const {
    messages,
    isLoading,
    onButtonPress,
    onClearConversationPress,
    onSplitAdd,
    isCreatingSplit,
    splitCreateError,
    isSplitAdded,
    onSplitUndo,
    sendMessage,
    handleSaveFileImage,
    fileImagesPreview,
    setFileImagesPreview,
    stopMessage,
    openFreshChat,
    walkThroughTheApp,
    hideDisclaimer,
    isSupportLoading,
    onButtonDeepLinkPress,
    onFollowUpOptionPress,
    lastCreatedSplitId,
    shouldShowGroupActions,
    shouldShowShareLinkCard,
  } = useSplitBillsViewModelProvider();

  const {t} = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const onNavigateToGroup = useCallback(() => {
    navigate(`${GROUPS_ROUTES.DASHBOARD}?${searchParams.toString()}`);
  }, [navigate, searchParams]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const typingMessages = useMemo(
    () => [
      {
        text: t('split_bills_page.bot_typing'),
      },
      {
        text: t('split_bills_page.bot_typing_2'),
      },
    ],
    [t],
  );

  const [typingIdx, setTypingIdx] = useState(0);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isLoading) return;
    setTypingIdx(0);

    const id = setInterval(() => {
      setTypingIdx(i => (i + 1) % typingMessages.length);
    }, 12000);

    return () => clearInterval(id);
  }, [isLoading, typingMessages]);

  return (
    <>
      <div className="chatbot-messages">
        {messages.map(msg => (
          <MessageBubble
            key={msg.id}
            text={msg.text}
            from={msg.from as SENDER.USER | SENDER.BOT}
            result={msg.result}
            splitBill={msg.splitBill}
            onButtonPress={onButtonPress}
            onClearConversationPress={onClearConversationPress}
            onSplitAdd={onSplitAdd}
            onSplitUndo={onSplitUndo}
            isCreatingSplit={isCreatingSplit}
            splitCreateError={splitCreateError}
            isSplitAdded={isSplitAdded}
            filePreview={msg.filePreview || null}
            showSupport={msg.showSupport}
            onButtonSupportPress={openFreshChat}
            onButtonWalkThroughPress={walkThroughTheApp}
            isSupportLoading={isSupportLoading}
            buttons={msg.buttons}
            onButtonLinkPress={onButtonDeepLinkPress}
            onFollowUpOptionPress={option =>
              onFollowUpOptionPress(option, msg.id)
            }
            selectedInMessageOption={msg.selectedInMessageOption}
          />
        ))}
        {isSplitAdded && lastCreatedSplitId && shouldShowShareLinkCard && (
          <div className="chat-bubble-wrapper bot">
            <img
              src={MiaLoadingAvatarV1}
              alt="Mia avatar"
              className="chat-bubble-avatar"
            />
            <div className="chat-bubble-block">
              <div className="chat-bubble bot">
                <SplitShareLinkCard
                  splitId={lastCreatedSplitId}
                  groupId={groupId}
                  groupName={groupName}
                  inviterName={inviterName}
                />
              </div>
            </div>
          </div>
        )}

        {isSplitAdded && lastCreatedSplitId && shouldShowGroupActions && (
          <div className="split-result-group-actions-wrapper">
            <SplitResultGroupActions
              onImDonePress={onNavigateToGroup}
              onNewSplitPress={onClearConversationPress}
              imDoneLabel={t('split_bills_page.im_done')}
              newSplitLabel={t('split_bills_page.new_split')}
            />
          </div>
        )}
        {isLoading && <TypingBubble text={typingMessages[typingIdx].text} />}
        <div ref={messagesEndRef} />
      </div>
      <MessageInput
        sendMessage={sendMessage}
        handleSaveFileImage={handleSaveFileImage}
        fileImagesPreview={fileImagesPreview}
        setFileImagesPreview={setFileImagesPreview}
        isLoading={isLoading}
        stopMessage={stopMessage}
        shouldShowDisclaimer={hideDisclaimer}
      />
    </>
  );
};
